import { createMachine, sendParent, sendTo } from "xstate";
import * as X2j from "xml-js";
import * as HorizonAccessorStates from "../constants/horizonAccessorStates";
import * as HorizonAccessorEvents from "../constants/horizonAccessorEvents";
import * as HorizonEvents from "../constants/horizonEvents";
import * as LoggingUtils from "./utils/logging";

export const functions = {
    SIGN_COLLECTIONS: "SIGN_COLLECTIONS",
    ROLE_RIGHTS: "ROLE_RIGHTS",
    USER_SETTINGS_SAVE: "USER_SETTINGS_SAVE",
    USER_SETTINGS_RESET: "USER_SETTINGS_RESET",
    WIDGET_INSTANCE_SAVE: "WIDGET_INSTANCE_SAVE",
};

const debug = process.env.NODE_ENV !== "production";

const getSuccessEvent = function (machineFunction) {
    switch (machineFunction) {
        case functions.SIGN_COLLECTIONS:
            return HorizonEvents.SIGN_COLLECTIONS_SET;
        case functions.ROLE_RIGHTS:
            return HorizonEvents.ROLE_RIGHT_SET;
        case functions.USER_SETTINGS_SAVE:
        case functions.USER_SETTINGS_RESET:
            return HorizonEvents.OCCUPANCY_SETTINGS_SET_DB;
        case functions.WIDGET_INSTANCE_SAVE:
        default:
            return HorizonEvents.NO_FURTHER_ACTION;
    }
};

const getErrorEvent = function (machineFunction) {
    switch (machineFunction) {
        case functions.USER_SETTINGS_SAVE:
        case functions.USER_SETTINGS_RESET:
            return HorizonEvents.OCCUPANCY_SETTINGS_ERROR;
        case functions.WIDGET_INSTANCE_SAVE:
        case functions.SIGN_COLLECTIONS:
        case functions.ROLE_RIGHTS:
        default:
            return HorizonEvents.HORIZON_ACCESSOR_ERRORED;
    }
};

export const horizonAccessorMachineConstructor = (
    id,
    machineFunction,
    additionalData
) => {
    return createMachine(
        {
            id: `horizonAccessorMachine_${id}`,
            context: {
                id,
                machineFunction,
                additionalData,
            },
            initial: HorizonAccessorStates.LOAD,
            invoke: {
                id: `loadingService_${id}`,
                src: () => (cb, onReceive) => {
                    onReceive((event) => {
                        const successFunction = (x) => {
                            cb({
                                type: HorizonAccessorEvents.HORIZON_ACCESSOR_SUCCESS,
                                data: x,
                            });
                        };
                        const errorFunction = (x) => {
                            cb({
                                type: HorizonAccessorEvents.HORIZON_ACCESSOR_ERRORED,
                                error: x,
                            });
                        };
                        switch (event.type) {
                            case functions.SIGN_COLLECTIONS:
                                AcisHorizon.Web.Framework.WidgetService.GetSignCollections(
                                    successFunction,
                                    errorFunction
                                );
                                break;
                            case functions.ROLE_RIGHTS:
                                AcisHorizon.Web.Framework.WidgetService.DoesUserHaveRoleRight(
                                    event.additionalData.roleRight,
                                    successFunction,
                                    errorFunction
                                );
                                break;
                            case functions.USER_SETTINGS_SAVE:
                                AcisHorizon.Web.Framework.WidgetService.SetUserSettingsElements(
                                    event.additionalData.group,
                                    X2j.js2xml(event.additionalData.settings, {
                                        compact: true,
                                    }),
                                    successFunction,
                                    errorFunction
                                );
                                break;
                            case functions.USER_SETTINGS_RESET:
                                AcisHorizon.Web.Framework.WidgetService.DeleteUserSettingsForGroup(
                                    event.additionalData.group,
                                    successFunction,
                                    errorFunction
                                );
                                break;
                            case functions.WIDGET_INSTANCE_SAVE:
                                AcisHorizon.Web.Framework.WidgetService.SaveWidgetState(
                                    event.additionalData.widgetId,
                                    X2j.js2xml(
                                        {
                                            state: {
                                                ...event.additionalData
                                                    .instanceState,
                                            },
                                        },
                                        {
                                            compact: true,
                                        }
                                    ),
                                    successFunction,
                                    errorFunction
                                );
                                break;
                            default:
                                break;
                        }
                    });
                },
            },
            states: {
                LOAD: {
                    entry: sendTo(`loadingService_${id}`, (context) => {
                        if (debug) {
                            LoggingUtils.default.logMachineEvent(
                                `Accessor: ${id}`,
                                context.machineFunction,
                                ""
                            );
                        }
                        return {
                            type: context.machineFunction,
                            additionalData: context.additionalData,
                        };
                    }),
                    always: HorizonAccessorStates.LOADING,
                },
                LOADING: {
                    on: {
                        HORIZON_ACCESSOR_SUCCESS: {
                            actions: [
                                "log",
                                sendParent((context, event) => ({
                                    type: getSuccessEvent(
                                        context.machineFunction
                                    ),
                                    id: context.id,
                                    machineFunction: context.machineFunction,
                                    additionalData: context.additionalData,
                                    data: event.data,
                                })),
                            ],
                            target: HorizonAccessorStates.DONE,
                        },
                        HORIZON_ACCESSOR_ERRORED: {
                            actions: [
                                "log",
                                sendParent((context, event) => ({
                                    type: getErrorEvent(
                                        context.machineFunction
                                    ),
                                    id: context.id,
                                    machineFunction: context.machineFunction,
                                    additionalData: context.additionalData,
                                    error: event.error,
                                })),
                            ],
                            target: HorizonAccessorStates.DONE,
                        },
                    },
                },
                DONE: {
                    entry: sendParent((context) => ({
                        type: HorizonEvents.MACHINE_KILL,
                        id: context.id,
                    })),
                },
            },
        },
        {
            actions: {
                log: (context, event) => {
                    LoggingUtils.default.logMachineEvent(
                        `Accessor: ${context.id}`,
                        context.machineFunction,
                        event.type
                    );
                },
            },
        }
    );
};
